import axios, { AxiosError } from 'axios';
import { useRuntimeConfig } from '#imports';
import {
  ApiConnectionError,
  ApiError,
  ApiRequestCanceledError,
  ApiSophonMfaRequiredError,
  ApiUnknownError,
} from '@/errors';

function isAxiosSophonError(error: AxiosError): boolean {
  /* v8 ignore start -- 呼び出し元のレスポンスインターセプターが err.response の有無を先に判定しているため、ここで response が undefined になる経路は存在しない */
  if (error.response === undefined) return false;
  /* v8 ignore stop */

  return Boolean(error.response.headers['x-sophon-error']);
}

export function useAxios() {
  const runtimeConfig = useRuntimeConfig();
  const $axios = axios.create({
    baseURL: runtimeConfig.public.BASE_URL_API,
    headers: {
      common: {
        'x-client-id': runtimeConfig.public.xClientId,
        'x-solution-id': runtimeConfig.public.xSolutionId,
      },
    },
  });

  $axios.interceptors.request.use(
    (config) => {
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  $axios.interceptors.response.use(
    (response) => {
      return response;
    },
    async (err) => {
      try {
        if (axios.isCancel(err)) {
          throw new ApiRequestCanceledError();
        }

        if (!err.response) {
          throw new ApiUnknownError();
        }

        const statusCode = err.response.status;
        if (statusCode === undefined) {
          throw new ApiConnectionError();
        }

        const responseError = (
          err.response?.data as { error?: { code?: string; message?: string } }
        )?.error;
        const errorCode = responseError?.code;
        const errorMessage = responseError?.message;

        if (isAxiosSophonError(err)) {
          const sophonErrorCode = err.response.headers['x-sophon-error'];

          if (sophonErrorCode === 'MFA_REQUIRED') {
            throw new ApiSophonMfaRequiredError(statusCode, errorCode, err, errorMessage);
          }
        }

        throw new ApiError(statusCode, errorCode, err, errorMessage);
      } catch (e) {
        return Promise.reject(e);
      }
    },
  );

  return { $axios };
}
